import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { firebase } from '@firebase/app';
import { auth } from 'firebase';
import { AngularFireAuth } from 'angularfire2/auth';

import { 
	AngularFirestore,
	AngularFirestoreDocument 
} from 'angularfire2/firestore';

import { Observable, of } from 'rxjs';
import { switchMap, startWith, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';

interface User {
	uid: string;
	email: string;
	photoURL?: string;
	displayName?: string;
	primaryPhone?: string;
	secondaryPhone?: string;
	address?: {
		streetOne?: string;
		streetTwo?: string;
		city?: string;
		state?: string;
		zip?: string;
	};
	rid?: string;
}

@Injectable()
export class AuthenticationService {

	user: Observable<User | null>;
	authState: any = null;

	constructor(
		private afAuth: AngularFireAuth,
		private afs: AngularFirestore,
		private router: Router,
		private spinner: NgxSpinnerService
	) {
		this.user = this.afAuth.authState.pipe(
			switchMap(user => {
				if (user) {
					return this.afs.doc<User>(`users/${user.uid}`).valueChanges();
				} else {
					return of(null);
				}
			})
			// tap(user => localStorage.setItem('user', JSON.stringify(user))),
  		// startWith(JSON.parse(localStorage.getItem('user')))
		);

		this.afAuth.authState.subscribe((auth) => {
			this.authState = auth;
		});
	}

	// Returns true if user is logged in
	get authenticated(): boolean {
		return this.authState !== null;
	}

	// Returns current user data
	get currentUser(): any {
		return this.authenticated ? this.authState : null;
	}
	
	////// OAuth Methods /////
	googleLogin() {
		const provider = new auth.GoogleAuthProvider();
		return this.oAuthLogin(provider);
	}

	facebookLogin() {
		const provider = new auth.FacebookAuthProvider();
		return this.oAuthLogin(provider);
	}

	twitterLogin() {
		const provider = new auth.TwitterAuthProvider();
		return this.oAuthLogin(provider);
	}

	private oAuthLogin(provider: any) {
		this.spinner.show();
		return this.afAuth.auth.signInWithPopup(provider).then(async credential => {	
			try {
				const doc = await this.afs.doc(`users/${credential.user.uid}`).ref.get();
				console.log('doc: ', doc);
				if (doc.exists) {
					console.log('User exists!');
				} else {
					console.log('User does not exist');
				}
			} catch (error) {
				console.log('Error...');
			}				

			this.updateUserData(credential.user);
			if (credential.additionalUserInfo.isNewUser) {
				this.router.navigate(['/account', { newuser: true }]);
			} else {
				this.user.subscribe(user => {
					if (!user) return false;
					if (user.rid && !user.primaryPhone) {
						this.router.navigate(['/account', { newuser: true }]);
					} else {
						this.router.navigate(['dashboard']);
					}
				});
			}
		}).catch(error => {
			console.log('Error: ', error);
			this.spinner.hide();
		});
	}

	//// Email/Password Auth ////
	emailSignUp(email, password, fullName?) {
		// return this.afAuth.auth.createUserWithEmailAndPassword(email, password);		
		return this.afAuth.auth
			.createUserWithEmailAndPassword(email, password)
			.then((user) => {			
				return this.currentUser.updateProfile({
					displayName: fullName || ''
				}).then(() => {
					return user;
				});
		});
	}

	sendEmailVerification() {
		this.afAuth.authState.subscribe(user => {
			user.sendEmailVerification().then(() => {
				console.log('email sent');
			});
		});
	}

	emailLogin(email: string, password: string) {
		return this.afAuth.auth.signInWithEmailAndPassword(email, password);
	}

	// Sends email allowing user to reset password
	resetPassword(email: string) {
		const fbAuth = auth();
		return fbAuth.sendPasswordResetEmail(email);
			// .catch(error => this.handleError(error));
	}

	updatePassword(password) {
		return this.currentUser.updatePassword(password);
	}

	logout() {
		this.afAuth.auth.signOut().then(() => {
			this.router.navigate(['login']);
		});
	}

	// Update properties on the user document
	updateUser(user: User, data: any) {
		return this.afs
			.doc(`users/${user.uid}`)
			.set(Object.assign({}, data), { merge: true });
	}

	updateUserData(user: User) {
		const userRef: AngularFirestoreDocument<User> = this.afs.doc(
			`users/${user.uid}`
		);
		const data: User = {
			uid: user.uid,
			email: user.email
		};
		// return userRef.set(Object.assign({}, data));
		return userRef.set(data, { merge: true });
	}

	signOut() {
		this.afAuth.auth.signOut().then(() => {
			this.router.navigate(['login']);
		});
	}

	getCurrentUser() {
		return this.afAuth.authState;
	}

	// Will use BehaviorSubject here to get current value without subscription	
	getUserSnapshot() {
		return this.authState;
	}

	get currentUserSnap() {
		return this.afAuth.auth.currentUser;
	}

	getUserRef(uid?) {
		const userId = uid || this.authState.uid;
		return this.afs.doc(`users/${userId}`).ref;
	}

	// If error, console log and notify user
	private handleError(error: Error) {
		console.error(error);
	}
}
